import { useEffect, useState } from 'react';
import { Package, X, Loader2, Upload } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { useMasterItems, useSpecialMasterItems } from '@/features/master-data/hooks/useMasters';
import { useUnits, useProductFacets } from '@/features/catalog/hooks/useCatalogApi';
import { useHasPermission } from '@/shared/permissions/useHasPermission';
import { masterId, type Item } from '../types/item';

// ── Item type config (kept local — only this form needs the icon/label map) ─
const ITEM_TYPE_OPTIONS: Record<string, string> = {
  stock_item: '📦 Stock Item',
  non_stock: '🔲 Non-Stock',
  service: '⚙️ Service',
  raw_material: '🛢️ Raw Material',
  intermediate: '🔧 Intermediate',
  finished_good: '✅ Finished Good',
  by_product: '♻️ By-Product',
  consumable: '🧪 Consumable',
  packaging: '📫 Packaging',
  // A sample pack is its own SKU with its own stock — never a flag on a
  // sellable product. Only these appear on the Sample form.
  sample: '🧴 Sample',
  subscription: '🔄 Subscription',
  media_spend: '📺 Media Spend',
  fixed_deliverable: '📋 Fixed Deliverable',
};

function emptyForm(defaultItemType: string) {
  return {
    name: '', sku: '', barcode: '', hsnSac: '', description: '', itemType: defaultItemType,
    divisionId: '', categoryMasterId: '', groupId: '', gradeId: '', packSizeId: '',
    selectorId: '', qtyPerPack: 0, totalPackSize: '', packQuantity: 0,
    unitId: '', image: '',
    isPurchased: true, isSold: true, isProduced: false,
    mrp: 0, minSellingPriceMinor: 0, purchasePriceMinor: 0, standardCostMinor: 0, sellingPriceMinor: 0,
    tax1Id: '', tax2Id: '',
    minQty: 0, maxQty: 0,
    isStockManaged: true, trackBatch: false, trackSerial: false, trackExpiry: false,
    valuationMethod: 'fifo', reorderPoint: 0, safetyStock: 0, leadTimeDays: 0,
    weightKg: 0, volumeLiters: 0,
    billableUnit: '', durationMinutes: 0, requiredSkill: '', timesheetLinked: false,
  };
}

function itemToForm(item: Item) {
  return {
    name: item.name || '', sku: item.sku || '', barcode: item.barcode || '', hsnSac: item.hsnSac || '',
    description: item.description || '', itemType: item.itemType || 'stock_item',
    divisionId: masterId(item.divisionId), categoryMasterId: masterId(item.categoryMasterId),
    groupId: masterId(item.groupId), gradeId: masterId(item.gradeId),
    packSizeId: masterId(item.packSizeId), selectorId: masterId(item.selectorId),
    qtyPerPack: item.qtyPerPack || 0, totalPackSize: item.totalPackSize || '',
    packQuantity: item.packQuantity || 0,
    unitId: item.unitId || '', image: item.images?.[0] || item.image || '',
    isPurchased: item.isPurchased !== false, isSold: item.isSold !== false, isProduced: !!item.isProduced,
    mrp: item.mrp || 0, minSellingPriceMinor: item.minSellingPriceMinor || 0,
    purchasePriceMinor: item.purchasePriceMinor || 0, standardCostMinor: item.standardCostMinor || 0,
    sellingPriceMinor: item.sellingPriceMinor || 0,
    tax1Id: item.tax1Id || '', tax2Id: item.tax2Id || '',
    minQty: item.minQty || 0, maxQty: item.maxQty || 0,
    isStockManaged: item.isStockManaged !== false, trackBatch: !!item.trackBatch, trackSerial: !!item.trackSerial,
    trackExpiry: !!item.trackExpiry, valuationMethod: item.valuationMethod || 'fifo',
    reorderPoint: item.reorderPoint || 0, safetyStock: item.safetyStock || 0, leadTimeDays: item.leadTimeDays || 0,
    weightKg: item.weightKg || 0, volumeLiters: item.volumeLiters || 0,
    billableUnit: item.billableUnit || '', durationMinutes: item.durationMinutes || 0,
    requiredSkill: item.requiredSkill || '', timesheetLinked: !!item.timesheetLinked,
  };
}

/**
 * Ensure the currently-selected id still appears in a scoped options list,
 * even if it fell out of scope (e.g. editing an item whose grade is no
 * longer used by any OTHER product in its group, so the facet query no
 * longer surfaces it) — without this, opening Edit on such an item would
 * silently show a blank Grade/Pack Size instead of its real saved value.
 */
function withCurrentSelection(
  options: { _id: string; name: string }[] | undefined,
  currentId: string,
  currentName: string | undefined,
): { _id: string; name: string }[] {
  const list = options ?? [];
  if (!currentId || list.some((o) => o._id === currentId)) return list;
  return [...list, { _id: currentId, name: currentName || '(current value)' }];
}

type FormState = ReturnType<typeof emptyForm>;

interface ItemFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: (item: Item) => void;
  /** Present → edit; absent → create. */
  item?: Item | null;
  defaultItemType?: string;
}

export function ItemFormModal({ open, onClose, onSaved, item, defaultItemType = 'stock_item' }: ItemFormModalProps) {
  const isEdit = !!item;
  const [form, setForm] = useState<FormState>(() => item ? itemToForm(item) : emptyForm(defaultItemType));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) setForm(item ? itemToForm(item) : emptyForm(defaultItemType));
  }, [open, item, defaultItemType]);

  const { data: divisions = [] } = useMasterItems('product_division', undefined, true);
  // Division -> Category -> Group is a parentId chain in master_items.
  const { data: categories = [] } = useMasterItems('product_category', form.divisionId || '', true);
  const { data: groups = [] } = useMasterItems('product_group', form.categoryMasterId || '', true);
  const { data: selectors = [] } = useMasterItems('api_selector', undefined, true);
  // Grade/Pack Size are scoped to what already exists among products in the
  // selected Group (Pack Size further narrowed by Grade) — same reasoning as
  // the sales pipeline's product selector: a Coolant item shouldn't be
  // offered "NLGI 2" as a grade. Editing an item whose current grade/pack
  // size isn't in that scoped list (e.g. it was the only product using it and
  // got deactivated) still shows it — see gradeOptions/packSizeOptions below.
  const { data: facets } = useProductFacets(form.divisionId, form.groupId, form.gradeId, form.categoryMasterId);
  const gradeOptions = withCurrentSelection(facets?.grades, form.gradeId, item?.grade);
  const packSizeOptions = withCurrentSelection(facets?.packSizes, form.packSizeId, item?.packSize);
  const { data: units = [] } = useUnits();
  const { data: taxRates = [] } = useSpecialMasterItems('tax-rates');

  const isService = ['service', 'media_spend', 'fixed_deliverable', 'subscription'].includes(form.itemType);

  const set = (k: keyof FormState, v: any) => setForm(f => ({ ...f, [k]: v }));
  const canSetFloor = useHasPermission('pricing.set_floor');

  const setDivision = (divisionId: string) =>
    setForm(f => ({ ...f, divisionId, categoryMasterId: '', groupId: '', gradeId: '', packSizeId: '' }));
  const setCategory = (categoryMasterId: string) =>
    setForm(f => ({ ...f, categoryMasterId, groupId: '', gradeId: '', packSizeId: '' }));
  const setGroup = (groupId: string) => setForm(f => ({ ...f, groupId, gradeId: '', packSizeId: '' }));
  const setGrade = (gradeId: string) => setForm(f => ({ ...f, gradeId, packSizeId: '' }));

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/api/accounting/items/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set('image', res.data.data.url);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Item name is required'); return; }
    setSaving(true); setError('');
    const payload = {
      ...form,
      divisionId: form.divisionId || null,
      categoryMasterId: form.categoryMasterId || null,
      groupId: form.groupId || null,
      gradeId: form.gradeId || null,
      packSizeId: form.packSizeId || null,
      selectorId: form.selectorId || null,
      unitId: form.unitId || null,
      tax1Id: form.tax1Id || null,
      tax2Id: form.tax2Id || null,
      isStockManaged: !isService && form.isStockManaged,
    };
    try {
      const res = isEdit
        ? await api.put(`/api/accounting/items/${item!._id}`, payload)
        : await api.post('/api/accounting/items', payload);
      onSaved(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const inputCls = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100';
  const labelCls = 'block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1';
  const checkCls = 'h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500';
  const sectionTitleCls = 'text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 pb-1 border-b border-slate-100 dark:border-slate-800';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-indigo-600">
          <div className="flex items-center gap-2 text-white">
            <Package className="w-5 h-5" />
            <h2 className="text-lg font-bold">{isEdit ? 'Edit Item' : 'Create New Item'}</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-2 text-sm border border-red-200">{error}</div>}

          {/* Basic Details */}
          <div className="space-y-3">
            <h3 className={sectionTitleCls}>Basic Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className={labelCls}>Item Name *</label>
                <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Hydraulic Oil 68" required />
              </div>
              <div>
                <label className={labelCls}>SKU / Code</label>
                <input className={inputCls} value={form.sku} onChange={e => set('sku', e.target.value)} placeholder="Auto-generated if blank" />
              </div>
              <div>
                <label className={labelCls}>Barcode</label>
                <input className={inputCls} value={form.barcode} onChange={e => set('barcode', e.target.value)} placeholder="e.g. 8901234567890" />
              </div>
              <div>
                <label className={labelCls}>HSN Code</label>
                <input className={inputCls} value={form.hsnSac} onChange={e => set('hsnSac', e.target.value)} placeholder="e.g. 27101980" />
              </div>
              <div>
                <label className={labelCls}>Item Type *</label>
                <select className={inputCls} value={form.itemType} onChange={e => set('itemType', e.target.value)}>
                  {Object.entries(ITEM_TYPE_OPTIONS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Division</label>
                <select className={inputCls} value={form.divisionId} onChange={e => setDivision(e.target.value)}>
                  <option value="">Select division…</option>
                  {divisions.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Category</label>
                <select className={inputCls} value={form.categoryMasterId} onChange={e => setCategory(e.target.value)} disabled={!form.divisionId}>
                  <option value="">{form.divisionId ? 'Select category…' : 'Select a division first'}</option>
                  {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Group</label>
                <select className={inputCls} value={form.groupId} onChange={e => setGroup(e.target.value)} disabled={!form.categoryMasterId}>
                  <option value="">{form.categoryMasterId ? 'Select group…' : 'Select a category first'}</option>
                  {groups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Grade</label>
                <select className={inputCls} value={form.gradeId} onChange={e => setGrade(e.target.value)} disabled={!form.groupId}>
                  <option value="">{form.groupId ? 'Select grade…' : 'Select a group first'}</option>
                  {gradeOptions.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Pack Size</label>
                <select className={inputCls} value={form.packSizeId} onChange={e => set('packSizeId', e.target.value)} disabled={!form.groupId}>
                  <option value="">{form.groupId ? 'Select pack size…' : 'Select a group first'}</option>
                  {packSizeOptions.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Selector (API / OEM)</label>
                <select className={inputCls} value={form.selectorId} onChange={e => set('selectorId', e.target.value)}>
                  <option value="">Select selector…</option>
                  {selectors.map(sel => <option key={sel._id} value={sel._id}>{sel.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Unit</label>
                <select className={inputCls} value={form.unitId} onChange={e => set('unitId', e.target.value)}>
                  <option value="">Select unit…</option>
                  {units.map(u => <option key={u._id} value={u._id}>{u.name} ({u.shortName})</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Qty per Pack</label>
                <input
                  type="number"
                  min={0}
                  className={inputCls}
                  value={form.qtyPerPack}
                  onChange={e => set('qtyPerPack', Number(e.target.value))}
                  placeholder="e.g. 20 packs per case"
                />
              </div>
              <div>
                <label className={labelCls}>Total Pack (case size)</label>
                <input
                  className={inputCls}
                  value={form.totalPackSize}
                  onChange={e => set('totalPackSize', e.target.value)}
                  placeholder="e.g. 16 LTR"
                />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Image</label>
                <div className="flex gap-2 items-center">
                  <input
                    className={inputCls}
                    value={form.image}
                    onChange={e => set('image', e.target.value)}
                    placeholder="https://example.com/image.jpg or click Browse ->"
                  />
                  <label className="flex items-center gap-1.5 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/60 dark:hover:bg-indigo-900/60 text-indigo-600 dark:text-indigo-300 rounded-lg cursor-pointer text-xs font-semibold whitespace-nowrap transition-colors border border-indigo-200 dark:border-indigo-800">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {uploading ? 'Uploading...' : 'Browse...'}
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                  </label>
                </div>
                {form.image && (
                  <div className="mt-2 flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <img src={form.image} alt="Preview" className="w-10 h-10 object-cover rounded-md border border-slate-200 dark:border-slate-700" />
                    <span className="text-xs text-slate-500 dark:text-slate-400 truncate flex-1">{form.image}</span>
                    <button type="button" onClick={() => set('image', '')} className="text-xs text-red-500 hover:text-red-700 font-medium">Remove</button>
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Description</label>
                <textarea className={inputCls} rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Short description..." />
              </div>
            </div>
          </div>

          {/* Procurement / Sales / Production flags */}
          <div>
            <label className={labelCls}>Procurement / Sales / Production</label>
            <div className="flex flex-wrap gap-4 mt-1">
              {[['isPurchased', 'Purchased'], ['isSold', 'Sold'], ['isProduced', 'Produced']].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input type="checkbox" className={checkCls} checked={!!form[k as keyof FormState]}
                    onChange={e => set(k as keyof FormState, e.target.checked)} />
                  {l}
                </label>
              ))}
            </div>
          </div>

          {/* Pricing Details */}
          <div className="space-y-3">
            <h3 className={sectionTitleCls}>Pricing Details</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Bottom Price (₹)</label>
                <input type="number" min="0" step="0.01" className={inputCls}
                  value={(form.minSellingPriceMinor / 100) || ''}
                  onChange={e => set('minSellingPriceMinor', Math.round(parseFloat(e.target.value || '0') * 100))}
                  disabled={!canSetFloor} readOnly={!canSetFloor}
                  placeholder="0.00" />
                {!canSetFloor && <p className="mt-1 text-xs text-slate-400">Only a Super Admin can change the floor price.</p>}
              </div>
              <div>
                <label className={labelCls}>MRP (₹)</label>
                <input type="number" min="0" step="0.01" className={inputCls}
                  value={form.mrp || ''} onChange={e => set('mrp', parseFloat(e.target.value || '0'))} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Purchase Price (₹)</label>
                <input type="number" min="0" step="0.01" className={inputCls}
                  value={(form.purchasePriceMinor / 100) || ''}
                  onChange={e => set('purchasePriceMinor', Math.round(parseFloat(e.target.value || '0') * 100))}
                  placeholder="0.00" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Selling Price (₹)</label>
                <input type="number" min="0" step="0.01" className={inputCls}
                  value={(form.sellingPriceMinor / 100) || ''}
                  onChange={e => set('sellingPriceMinor', Math.round(parseFloat(e.target.value || '0') * 100))}
                  placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Standard Cost (₹)</label>
                <input type="number" min="0" step="0.01" className={inputCls}
                  value={(form.standardCostMinor / 100) || ''}
                  onChange={e => set('standardCostMinor', Math.round(parseFloat(e.target.value || '0') * 100))}
                  placeholder="0.00" />
              </div>
            </div>
            {form.sellingPriceMinor > 0 && form.purchasePriceMinor > 0 && (
              <div className="text-xs text-slate-500 flex gap-4">
                <span>Gross Margin: <strong className="text-emerald-600">{(((form.sellingPriceMinor - form.purchasePriceMinor) / form.sellingPriceMinor) * 100).toFixed(1)}%</strong></span>
                <span>Markup: <strong className="text-indigo-600">{(((form.sellingPriceMinor - form.purchasePriceMinor) / form.purchasePriceMinor) * 100).toFixed(1)}%</strong></span>
              </div>
            )}
          </div>

          {/* Tax */}
          <div className="space-y-3">
            <h3 className={sectionTitleCls}>Tax</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Tax 1</label>
                <select className={inputCls} value={form.tax1Id} onChange={e => set('tax1Id', e.target.value)}>
                  <option value="">None</option>
                  {taxRates.map(t => <option key={t._id} value={t._id}>{t.name} — {String(t.rate)}% {String(t.type)}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Tax 2</label>
                <select className={inputCls} value={form.tax2Id} onChange={e => set('tax2Id', e.target.value)}>
                  <option value="">None</option>
                  {taxRates.map(t => <option key={t._id} value={t._id}>{t.name} — {String(t.rate)}% {String(t.type)}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Stock settings — hide for services */}
          {!isService && (
            <div>
              <label className={labelCls}>Stock Management</label>
              <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl p-4 space-y-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className={checkCls} checked={form.isStockManaged} onChange={e => set('isStockManaged', e.target.checked)} />
                  Track stock levels
                </label>
                {form.isStockManaged && (
                  <>
                    <div className="flex flex-wrap gap-3">
                      {[['trackBatch', 'Batch/Lot'], ['trackSerial', 'Serial/IMEI'], ['trackExpiry', 'Expiry Date']].map(([k, l]) => (
                        <label key={k} className="flex items-center gap-2 text-sm">
                          <input type="checkbox" className={checkCls} checked={!!form[k as keyof FormState]} onChange={e => set(k as keyof FormState, e.target.checked)} />
                          {l}
                        </label>
                      ))}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[['reorderPoint', 'Reorder Point'], ['safetyStock', 'Safety Stock'], ['leadTimeDays', 'Lead Time (days)']].map(([k, l]) => (
                        <div key={k}>
                          <label className="text-xs text-slate-500 mb-1 block">{l}</label>
                          <input type="number" min="0" className={inputCls} value={Number(form[k as keyof FormState]) || ''}
                            onChange={e => set(k as keyof FormState, Number(e.target.value))} />
                        </div>
                      ))}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Valuation Method</label>
                      <select className={inputCls} value={form.valuationMethod} onChange={e => set('valuationMethod', e.target.value)}>
                        <option value="fifo">FIFO</option>
                        <option value="wavg">Weighted Average</option>
                        <option value="standard">Standard Cost</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Service fields */}
          {isService && (
            <div>
              <label className={labelCls}>Service Details</label>
              <div className="bg-violet-50 dark:bg-violet-950/30 rounded-xl p-4 grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Billable Unit</label>
                  <select className={inputCls} value={form.billableUnit} onChange={e => set('billableUnit', e.target.value)}>
                    <option value="">Select…</option>
                    {['hour', 'day', 'project', 'unit', 'session'].map(u => <option key={u} value={u}>{u.charAt(0).toUpperCase() + u.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Duration (minutes)</label>
                  <input type="number" min="0" className={inputCls} value={form.durationMinutes || ''} onChange={e => set('durationMinutes', Number(e.target.value))} />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Required Skill / Role</label>
                  <input className={inputCls} value={form.requiredSkill} onChange={e => set('requiredSkill', e.target.value)} placeholder="e.g. Senior Developer" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" className={checkCls} checked={form.timesheetLinked} onChange={e => set('timesheetLinked', e.target.checked)} />
                    Link Timesheets
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Physical attributes */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Weight (kg)</label>
              <input type="number" min="0" step="0.001" className={inputCls} value={form.weightKg || ''} onChange={e => set('weightKg', Number(e.target.value))} />
            </div>
            <div>
              <label className={labelCls}>Volume (liters)</label>
              <input type="number" min="0" step="0.001" className={inputCls} value={form.volumeLiters || ''} onChange={e => set('volumeLiters', Number(e.target.value))} />
            </div>
          </div>

          {/* Additional Details */}
          <div className="space-y-3">
            <h3 className={sectionTitleCls}>Additional Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Minimum Reorder Stock</label>
                <input type="number" min="0" className={inputCls} value={form.minQty || ''} onChange={e => set('minQty', Number(e.target.value))} />
              </div>
              <div>
                <label className={labelCls}>Maximum Reorder Stock</label>
                <input type="number" min="0" className={inputCls} value={form.maxQty || ''} onChange={e => set('maxQty', Number(e.target.value))} />
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">Cancel</button>
          <button type="button" onClick={handleSubmit} disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2 transition-colors disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Item'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ItemFormModal;
